import type { FC } from 'react';
import Button from '@components/Button';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { MODAL_Z_INDEX } from '@constants/z-indices';
import { useEntityStore } from '@ContractBuilder/store';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationIcon, XIcon } from '@heroicons/react/outline';
import clsx from 'clsx';

interface UnsavedChangesModalProps {
  isSaveChangesDisabled?: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onDisregardChanges: () => void;
  onSaveChanges: () => void | Promise<false | void>;
  saveChangesDisabledTooltip?: string;
}

export const UnsavedChangesModal: FC<UnsavedChangesModalProps> = ({
  isSaveChangesDisabled = false,
  isOpen,
  onCancel,
  onDisregardChanges,
  onSaveChanges,
  saveChangesDisabledTooltip,
}) => {
  const { isLoading } = useEntityStore(({ isLoading }) => ({ isLoading }));

  return (
    <Show when={isOpen}>
      <Dialog
        open={isOpen}
        as="div"
        className={clsx('fixed inset-0 overflow-y-auto', MODAL_Z_INDEX)}
        data-testid="unsaved-changes-modal"
        onClose={onCancel}
      >
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="bg-info-500/75 fixed inset-0 transition-opacity" />

          <DialogPanel
            transition
            className="flex h-screen items-center justify-center duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0"
          >
            <div className="relative inline-block w-[600px] transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
              <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                <button
                  type="button"
                  className="text-info-400 hover:text-info-500 rounded-md bg-white focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:outline-hidden"
                  onClick={onCancel}
                >
                  <span className="sr-only">Cancel</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <DialogTitle as="h3" className="text-info-800 text-lg leading-6 font-medium">
                    You have unsaved changes
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-info-500 text-sm leading-5 font-normal">
                      Would you like to save your changes before leaving?
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-12 items-center justify-end gap-x-2.5 sm:mt-10 sm:flex">
                <Button
                  className="mr-auto ml-14 h-auto px-4 py-2"
                  onClick={onDisregardChanges}
                  kind="secondary"
                  size="sm"
                  isDisabled={isLoading}
                >
                  Discard changes
                </Button>
                <Button
                  className="h-auto px-4 py-2"
                  onClick={onCancel}
                  kind="secondary"
                  size="sm"
                  isDisabled={isLoading}
                >
                  Cancel
                </Button>
                <Tooltip content={saveChangesDisabledTooltip} disabled={!isSaveChangesDisabled}>
                  <div className="w-fit">
                    <Button
                      className="h-auto px-4 py-2"
                      isDisabled={isSaveChangesDisabled}
                      kind="primary"
                      loading={isLoading}
                      onClick={onSaveChanges}
                      size="sm"
                    >
                      Save changes
                    </Button>
                  </div>
                </Tooltip>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </Show>
  );
};
